/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { isAuthRequiredResponse } from "../../axiosConfig/axiosInterceptor";
import {
	ICircleLeaderApplication,
	IAccountVerificationPayload,
} from "../../utility/types/circleType";
import {
	denyAddRequestService,
	submitCircleLeaderApplication,
	submitAccountVerification,
	submitCreateCircle,
	submitFetchAddRequest,
	submitFetchAllCircles,
	submitFetchCircle,
	submitFetchCircleByLeader,
	submitLeaderSendAddRequest,
	submitUpdateCircle,
	submitViewAndSearchAddRequestSendOut,
	submitAcceptAddRequest,
	submitFetchCircleById,
	submitGetAccountVerificationStatus,
	submitJoinCircleRequest,
	submitFetchJoinCircleRequestsOfCircle,
	submitFetchJoinCircleRequestsOfCircleByCircleLeaderId,
	submitAcceptJoinRequest,
	submitDenyJoinRequest,
	submitGetCircleConnectedAccountBalance,
	submitGetCircleExpectedTotalPayOut,
	submitGetIdentityVerificationSession,
	completeIdentityVerificationSession,
	fetchIdentityVerificationStatus,
	submitJoinRequestUserSent,
	submitCreateAccountLink,
	submitAccountRequirements,
	submitUpdateAccountRequirements,
	submitGetUnionCycleBalances,
	getAllCirclesCurrentMonthContribution,
	getPublicStats,
	getContributionsChartData,
	getAllUnionLeaderApplications,
	approveUnionLeaderApplication,
	rejectUnionLeaderApplication,
	deleteUnionLeaderApplication,
	submitAdminFetchAllCircles,
	submitAdminFetchCircleById,
	submitAdminUpdateCircleById,
	submitAdminDeleteCircleById,
	submitAdminBanCircle,
	submitAdminUnbanCircle,
	getAllJoinRequestsAsAdmin,
	approveJoinRequestAsAdmin,
	rejectJoinRequestAsAdmin,
	getAllAddRequestsAsAdmin,
	approveAddRequestAsAdmin,
	rejectAddRequestAsAdmin,
	getAllCirclesContributionHistory,
	submitFetchCirclePayoutOrder,
} from "../services/circleService";
import { ViewAndSearchAddRequestSendOutQueryPayload } from "../../utility/types/addRequestType";
import { IJoinRequestPayload } from "../../utility/types/joinRequestType";
import axios from "axios";

export const circleLeaderApplication = createAsyncThunk(
	"circle/leader-application",
	async (payload: ICircleLeaderApplication, { rejectWithValue }) => {
		try {
			const response = await submitCircleLeaderApplication(payload);

			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred",
			);
		}
	},
);

export const accountVerification = createAsyncThunk(
	"account-verification",
	async (payload: IAccountVerificationPayload, { rejectWithValue }) => {
		try {
			const response = await submitAccountVerification(payload);

			return response.data;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getAccountVerificationStatus = createAsyncThunk(
	"account-verification/status",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitGetAccountVerificationStatus(userId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const createCircle = createAsyncThunk(
	"circle/create",
	async (payload: FormData, { rejectWithValue }) => {
		try {
			const response = await submitCreateCircle(payload);

			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchCircleByLeader = createAsyncThunk(
	"circle/all-leader-circle",
	async (leaderId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchCircleByLeader(leaderId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchCircleById = createAsyncThunk(
	"circle/circleById",
	async (unionId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchCircleById(unionId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchCircle = createAsyncThunk(
	"circle/memberCircle",
	async (
		{ userId, circleId }: { userId: string; circleId: string[] | any },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitFetchCircle(userId, circleId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchAddRequest = createAsyncThunk(
	"member/add-request",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchAddRequest(userId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const acceptAddRequest = createAsyncThunk(
	"member/acceptAddRequest",
	async (
		{
			requestId,
			hasAgreedToCircleTerms,
		}: {
			requestId: string;
			hasAgreedToCircleTerms: boolean;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await submitAcceptAddRequest(
				requestId,
				hasAgreedToCircleTerms,
			);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const denyAddRequest = createAsyncThunk(
	"member/denyAddRequest",
	async (requestId: string, { rejectWithValue }) => {
		try {
			const response = await denyAddRequestService(requestId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchAllCircles = createAsyncThunk(
	"allCircle",
	async (_, { rejectWithValue }) => {
		try {
			const response = await submitFetchAllCircles();
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchAllCirclesAdmin = createAsyncThunk(
	"admin/circles/all",
	async (_, { rejectWithValue }) => {
		try {
			const response = await submitAdminFetchAllCircles();
			// response: { total, circles, generatedAt }
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchCircleByIdAdmin = createAsyncThunk(
	"admin/circles/getById",
	async (circleId: string, { rejectWithValue }) => {
		try {
			const response = await submitAdminFetchCircleById(circleId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const updateCircleByIdAdmin = createAsyncThunk(
	"admin/circles/update",
	async (
		{ circleId, payload }: { circleId: string; payload: any },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitAdminUpdateCircleById(circleId, payload);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const deleteCircleByIdAdmin = createAsyncThunk(
	"admin/circles/delete",
	async (circleId: string, { rejectWithValue }) => {
		try {
			const response = await submitAdminDeleteCircleById(circleId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const banCircleAdmin = createAsyncThunk(
	"admin/circles/ban",
	async (
		{ circleId, reason }: { circleId: string; reason?: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitAdminBanCircle(circleId, reason);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const unbanCircleAdmin = createAsyncThunk(
	"admin/circles/unban",
	async (circleId: string, { rejectWithValue }) => {
		try {
			const response = await submitAdminUnbanCircle(circleId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchLeaderSendAddRequest = createAsyncThunk(
	"leaderSendAddRequest",
	async (
		{ unionId, memberToAddId }: { unionId: string; memberToAddId: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitLeaderSendAddRequest({
				unionId,
				memberToAddId,
			});
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const updateCircle = createAsyncThunk(
	"updateCircle",
	async (
		{ unionId, payload }: { unionId: string; payload: FormData }, // First argument is a single object containing all the parameters
		{ rejectWithValue },
	) => {
		try {
			const response = await submitUpdateCircle(unionId, payload);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchViewAndSearchAddRequestSendOut = createAsyncThunk(
	"viewAndSearchAddRequestSendOut",
	async (
		{
			unionId,
			searchValues,
		}: {
			unionId: string;
			searchValues?: ViewAndSearchAddRequestSendOutQueryPayload;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await submitViewAndSearchAddRequestSendOut(
				unionId,
				searchValues,
			);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const joinCircleRequest = createAsyncThunk(
	"circle/joinCircleRequest",
	async (
		{ payload, circleId }: { payload: IJoinRequestPayload; circleId: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitJoinCircleRequest(payload, circleId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchJoinCircleRequestsOfCircle = createAsyncThunk(
	"circle/joinCircleRequestOfCircle",
	async (circleId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchJoinCircleRequestsOfCircle(circleId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchJoinCircleRequestsOfCircleByCircleLeaderId = createAsyncThunk(
	"circle/joinCircleRequestOfCircleByCircleLeaderId",
	async (circleLeaderId: string, { rejectWithValue }) => {
		try {
			const response =
				await submitFetchJoinCircleRequestsOfCircleByCircleLeaderId(
					circleLeaderId,
				);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const fetchJoinRequestUserSent = createAsyncThunk(
	"circle/userSentJoinRequest",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await submitJoinRequestUserSent(userId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const acceptJoinRequest = createAsyncThunk(
	"member/acceptJoinRequest",
	async (
		{ circleId, requestId }: { circleId: string; requestId: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitAcceptJoinRequest(circleId, requestId);

			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const denyJoinRequest = createAsyncThunk(
	"member/denyJoinRequest",
	async (
		{ circleId, requestId }: { circleId: string; requestId: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitDenyJoinRequest(circleId, requestId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

// deprecated
export const getCircleConnectedAccountBalance = createAsyncThunk(
	"connectAccountBalance",
	async ({ unionId }: { unionId: string }, { rejectWithValue }) => {
		try {
			const response = await submitGetCircleConnectedAccountBalance(unionId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getUnionOrCircleCycleBalances = createAsyncThunk(
	"circleAccountBalance",
	async (
		{ unionId, cycleId }: { unionId: string; cycleId: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await submitGetUnionCycleBalances(unionId, cycleId);

			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getCircleExpectedTotalPayOut = createAsyncThunk(
	"expectedTotalPayOut",
	async ({ unionId }: { unionId: string }, { rejectWithValue }) => {
		try {
			const response = await submitGetCircleExpectedTotalPayOut(unionId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getIdentityVerificationSession = createAsyncThunk(
	"identityVerification/getSession",
	async ({ userId }: { userId: string }, { rejectWithValue }) => {
		try {
			const response = await submitGetIdentityVerificationSession(userId);
			return response;
		} catch (error: any) {
			const message =
				error.response?.data.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const getIdentityVerificationStatus = createAsyncThunk(
	"identityVerification/getStatus",
	async (
		{ userId, token }: { userId: string; token: string },
		{ rejectWithValue },
	) => {
		try {
			return await fetchIdentityVerificationStatus(userId, token);
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Unknown Error Occurred";
			return rejectWithValue(message);
		}
	},
);

export const completeIdentityVerification = createAsyncThunk(
	"identityVerification/completeSession",
	async ({ userId }: { userId: string }, { rejectWithValue }) => {
		try {
			const response = await completeIdentityVerificationSession(userId);

			return response;
		} catch (err: any) {
			let msg = "Unknown error completing verification";
			if (axios.isAxiosError(err)) {
				msg =
					(err.response?.data as any)?.message ||
					err.response?.statusText ||
					err.message;
			} else if (err instanceof Error) {
				msg = err.message;
			}
			return rejectWithValue({
				message: msg,
				httpStatus: err?.httpStatus,
				isBanned: err?.isBanned,
				bannedAt: err?.bannedAt,
				banReason: err?.banReason,
			});
		}
	},
);

export const createAccountLink = createAsyncThunk<
	{ accountLinkUrl: string; expiresAt?: number },
	string,
	{ rejectValue: string }
>("user/createAccountLink", async (userId, { rejectWithValue }) => {
	try {
		const { data } = await submitCreateAccountLink(userId);

		// The interceptor resolves 401s, so an absent URL here means the request never succeeded.
		if (!data?.accountOnboardingUrl) {
			return rejectWithValue(
				isAuthRequiredResponse(data) ?
					"Your session expired. Please sign in again to finish setting up your bank account."
					: "We could not start your bank setup. Please try again.",
			);
		}

		// Expect data = { accountLinkUrl: string, expiresAt: number }
		return {
			accountLinkUrl: data.accountOnboardingUrl,
			expiresAt: data.expiresAt,
		};
	} catch (err: any) {
		const msg =
			err.response?.data?.message ||
			err.message ||
			"Failed to create account link";
		return rejectWithValue(msg);
	}
});

export interface StripeAccountDetailsResponse {
	details: {
		external_account: any | null;
		representative: any | null;
	};
	requirements: {
		currently_due: string[];
		past_due: string[];
		eventually_due: string[];
	};
	identityVerificationStatus?:
	| "pending"
	| "processing"
	| "verified"
	| "requires_action"
	| "none";
	isUserVerified?: boolean;
	// Country-specific configuration for verification requirements
	countryConfig?: {
		code: string;
		name: string;
		requiresSSN: boolean;
		nationalIdLabel: string;
		currency: string;
	};
}

export const getAccountRequirement = createAsyncThunk<
	StripeAccountDetailsResponse,
	string,
	{ rejectValue: string }
>("stripe-account-requirements", async (userId, { rejectWithValue }) => {
	try {
		const response = await submitAccountRequirements(userId);
		return response.data;
	} catch (err: any) {
		const msg =
			err.response?.data?.message ||
			err.message ||
			"Failed to create account link";
		return rejectWithValue(msg);
	}
});

export const updateStripeAccountDetails = createAsyncThunk<
	void,
	{
		userId: string;
		ssn_last_4: string;
		dob: { day: number; month: number; year: number };
	},
	{ rejectValue: string }
>(
	"stripe/updateAccountDetails",
	async ({ userId, ssn_last_4, dob }, { rejectWithValue }) => {
		try {
			await submitUpdateAccountRequirements(userId, { ssn_last_4, dob });
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to update account",
			);
		}
	},
);

export const fetchAllCirclesCurrentMonthContribution = createAsyncThunk(
	"admin/allCirclesStats",
	async (_, { rejectWithValue }) => {
		try {
			const response = await getAllCirclesCurrentMonthContribution();

			return response.data;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to update account",
			);
		}
	},
);

export const fetchAllCirclesContributionHistory = createAsyncThunk(
	"admin/allCirclesStatsHistory",
	async (_, { rejectWithValue }) => {
		try {
			const response = await getAllCirclesContributionHistory();

			return response.contributionHistory;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to update account",
			);
		}
	},
);

// Public stats endpoint - no authentication required
export const fetchPublicStats = createAsyncThunk(
	"public/stats",
	async (_, { rejectWithValue }) => {
		try {
			const response = await getPublicStats();

			return response.data;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to fetch public stats",
			);
		}
	},
);

export const fetchContributionsChartData = createAsyncThunk(
	"admin/contributionsChart",
	async (_, { rejectWithValue }) => {
		try {
			const response = await getContributionsChartData();

			return response.data;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to fetch contributions chart data",
			);
		}
	},
);

// Fetch all union leader applications
export const fetchAllUnionLeaderApplications = createAsyncThunk(
	"admin/unionLeaderApplications",
	async (status: string | undefined, { rejectWithValue }) => {
		try {
			const response = await getAllUnionLeaderApplications(status);
			return response; // Service already returns response.data
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to fetch union leader applications",
			);
		}
	},
);

// Approve union leader application
export const approveApplication = createAsyncThunk(
	"admin/approveUnionLeaderApplication",
	async (applicationId: string, { rejectWithValue }) => {
		try {
			const response = await approveUnionLeaderApplication(applicationId);
			return response.data;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to approve application",
			);
		}
	},
);

// Reject union leader application
export const rejectApplication = createAsyncThunk(
	"admin/rejectUnionLeaderApplication",
	async (applicationId: string, { rejectWithValue }) => {
		try {
			const response = await rejectUnionLeaderApplication(applicationId);
			return response.data;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to reject application",
			);
		}
	},
);

// Delete union leader application
export const deleteApplication = createAsyncThunk(
	"admin/deleteUnionLeaderApplication",
	async (applicationId: string, { rejectWithValue }) => {
		try {
			const response = await deleteUnionLeaderApplication(applicationId);
			return response.data;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to delete application",
			);
		}
	},
);

// Fetch all join requests (admin only)
export const fetchAllJoinRequestsAsAdmin = createAsyncThunk(
	"admin/getAllJoinRequests",
	async (_, { rejectWithValue }) => {
		try {
			const response = await getAllJoinRequestsAsAdmin();
			return response;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to fetch join requests",
			);
		}
	},
);

// Approve join request (admin only)
export const approveJoinRequest = createAsyncThunk(
	"admin/approveJoinRequest",
	async (joinRequestId: string, { rejectWithValue }) => {
		try {
			const response = await approveJoinRequestAsAdmin(joinRequestId);
			return response;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to approve join request",
			);
		}
	},
);

// Reject join request (admin only)
export const rejectJoinRequest = createAsyncThunk(
	"admin/rejectJoinRequest",
	async (joinRequestId: string, { rejectWithValue }) => {
		try {
			const response = await rejectJoinRequestAsAdmin(joinRequestId);
			return response;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to reject join request",
			);
		}
	},
);

// Fetch all add requests (admin only)
export const fetchAllAddRequestsAsAdmin = createAsyncThunk(
	"admin/getAllAddRequests",
	async (_, { rejectWithValue }) => {
		try {
			const response = await getAllAddRequestsAsAdmin();
			return response;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to fetch add requests",
			);
		}
	},
);

// Approve add request (admin only)
export const approveAddRequest = createAsyncThunk(
	"admin/approveAddRequest",
	async (addRequestId: string, { rejectWithValue }) => {
		try {
			const response = await approveAddRequestAsAdmin(addRequestId);
			return response;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to approve add request",
			);
		}
	},
);

// Reject add request (admin only)
export const rejectAddRequest = createAsyncThunk(
	"admin/rejectAddRequest",
	async (addRequestId: string, { rejectWithValue }) => {
		try {
			const response = await rejectAddRequestAsAdmin(addRequestId);
			return response;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to reject add request",
			);
		}
	},
);
// Fetch payout order for a circle (accessible by any authenticated member of the circle)
export const fetchCirclePayoutOrder = createAsyncThunk(
	"circle/fetchCirclePayoutOrder",
	async (unionId: string, { rejectWithValue }) => {
		try {
			const response = await submitFetchCirclePayoutOrder(unionId);
			return response;
		} catch (err: any) {
			return rejectWithValue(
				err.response?.data?.message ||
				err.message ||
				"Failed to fetch payout order",
			);
		}
	},
);